import { useState, useEffect, useCallback } from 'react';

interface UseResponsiveProps {
    initialIsMobile?: boolean;
    breakpoint?: number;
    debounceDelay?: number;
}

interface ResponsiveState {
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    width: number | null;
    isClient: boolean;
}

export const useResponsive = ({
    initialIsMobile = false,
    breakpoint = 768,
    debounceDelay = 150,
}: UseResponsiveProps = {}): ResponsiveState => {
    const [isMobile, setIsMobile] = useState(initialIsMobile);
    const [width, setWidth] = useState<number | null>(null);
    const [isClient, setIsClient] = useState(false);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    // 防抖函数
    const debounce = useCallback((callback: () => void, delay: number) => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        const newTimer = setTimeout(() => {
            callback();
        }, delay);

        setDebounceTimer(newTimer);
    }, [debounceTimer]);

    // 检测屏幕尺寸的函数
    const checkScreenSize = useCallback(() => {
        const currentWidth = window.innerWidth;
        const newIsMobile = currentWidth < breakpoint;

        // 只有当状态真的发生变化时才更新
        if (newIsMobile !== isMobile || currentWidth !== width) {
            setIsMobile(newIsMobile);
            setWidth(currentWidth);
        }
    }, [isMobile, width, breakpoint]);

    // 使用防抖的resize处理函数
    const debouncedCheckScreenSize = useCallback(() => {
        debounce(checkScreenSize, debounceDelay);
    }, [checkScreenSize, debounce, debounceDelay]);

    useEffect(() => {
        setIsClient(true);

        // 初始检测
        checkScreenSize();

        // 添加监听器
        window.addEventListener('resize', debouncedCheckScreenSize);

        // 清理监听器
        return () => {
            window.removeEventListener('resize', debouncedCheckScreenSize);
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [checkScreenSize, debouncedCheckScreenSize, debounceTimer]);

    return {
        isMobile,
        isTablet: width ? width >= breakpoint && width < 1024 : false,
        isDesktop: width ? width >= 1024 : !initialIsMobile,
        width,
        isClient,
    };
};

export default useResponsive; 